import { motion } from "framer-motion";
import { useTranslation } from "@/packages/utils";
import { springs } from "../ui/motion";

const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  draft: {
    bg: "rgba(161,161,166,0.10)",
    text: "#a1a1a6",
    border: "rgba(161,161,166,0.25)",
  },
  submitted: {
    bg: "rgba(59,130,246,0.10)",
    text: "#3b82f6",
    border: "rgba(59,130,246,0.25)",
  },
  approved: {
    bg: "rgba(34,197,94,0.10)",
    text: "#22c55e",
    border: "rgba(34,197,94,0.25)",
  },
  rejected: {
    bg: "rgba(239,68,68,0.10)",
    text: "#ef4444",
    border: "rgba(239,68,68,0.25)",
  },
  archived: {
    bg: "rgba(161,161,166,0.10)",
    text: "#a1a1a6",
    border: "rgba(161,161,166,0.25)",
  },
};

function getStatusStyles(status: string) {
  return STATUS_STYLES[status.toLowerCase()] ?? STATUS_STYLES.draft;
}

function StatusBadge({
  label,
  status,
  animDelay,
}: {
  label: string;
  status: string;
  animDelay: number;
}) {
  const s = getStatusStyles(status);
  return (
    <motion.span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium border"
      style={{
        backgroundColor: s.bg,
        color: s.text,
        borderColor: s.border,
      }}
      initial={{ scale: 0.7, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ ...springs.bouncy, delay: animDelay }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ backgroundColor: s.text }}
      />
      {label}
    </motion.span>
  );
}

interface Props {
  from: string;
  to: string;
  changedBy?: string;
}

export function StatusTransitionWidget({ from, to, changedBy }: Props) {
  const { t } = useTranslation("inbox");
  const toStyles = getStatusStyles(to);

  const fromLabel = t(`statusValues.${from}`, { defaultValue: from });
  const toLabel = t(`statusValues.${to}`, { defaultValue: to });

  return (
    <motion.div
      className="flex items-center gap-3 mb-5 flex-wrap"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.smooth, delay: 0.1 }}
    >
      <StatusBadge label={fromLabel} status={from} animDelay={0.15} />

      {/* Animated connector dots */}
      <div className="flex items-center gap-[3px]">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-[3px] h-[3px] rounded-full"
            style={{ backgroundColor: toStyles.text }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.35 }}
            transition={{
              ...springs.snappy,
              delay: 0.22 + i * 0.05,
            }}
          />
        ))}
      </div>

      <StatusBadge label={toLabel} status={to} animDelay={0.32} />

      {changedBy && (
        <motion.span
          className="text-[11px] text-muted ml-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...springs.smooth, delay: 0.38 }}
        >
          {t("statusChange.by")}{" "}
          <span className="text-secondary font-medium">{changedBy}</span>
        </motion.span>
      )}
    </motion.div>
  );
}
